import { Component, DestroyRef, EventEmitter, inject, Input, Output, ChangeDetectionStrategy } from '@angular/core';
import { takeUntilDestroyed } from '@angular/core/rxjs-interop';
import {
  DynamicBoardCard,
  BoardManager,
  BoardField,
  CardViewer,
  BoardCardChat,
  BoardCompany,
  BoardContact,
} from '../board-v3.types';
import { BoardContactService } from '../../../services/board-contact.service';
import { BoardCompanyService } from '../../../services/board-company.service';
import { CHANNEL_TYPE, CHANNEL_TYPES_OBJECT, CURRENCY_SYMBOLS } from '../../../enum/constants';
import { environment } from '../../../../environments/environment';
import { formatMoneyAmount, formatPriceFieldValue, isPriceFieldValue } from '../price-field.utils';
import { formatFullName } from '../../../helpers';
import { getContactName } from '../contacts/contact.utils';
import { getCompanyName } from '../companies/company.utils';
import { AddressInput, getCardPhoneValue, ICardPhone } from '../../../interfaces/card';
import { formatAddress } from '../address.utils';

@Component({
  selector: 'app-board-v3-card-item',
  templateUrl: './card-item.component.html',
  styleUrls: ['./card-item.component.scss'],
  changeDetection: ChangeDetectionStrategy.Eager,
  standalone: false,
})
export class CardItemComponent {
  @Input({ required: true }) card!: DynamicBoardCard;
  @Input() viewFields: string[] = [];
  @Input() allFields: BoardField[] = [];
  @Input() canMove: boolean = false;
  @Input() compact: boolean = false;
  @Input() selectionMode: boolean = false;
  @Input() selected: boolean = false;

  @Output() cardClick = new EventEmitter<DynamicBoardCard>();
  @Output() cardDelete = new EventEmitter<DynamicBoardCard>();
  @Output() cardMoveClick = new EventEmitter<DynamicBoardCard>();
  @Output() chatClick = new EventEmitter<BoardCardChat>();
  @Output() selectionToggle = new EventEmitter<DynamicBoardCard>();

  protected readonly isTouchContextMenuDisabled = this.hasTouchPointer();

  private readonly contactService = inject(BoardContactService);
  private readonly companyService = inject(BoardCompanyService);
  private readonly destroyRef = inject(DestroyRef);

  private readonly KNOWN_KEYS = new Set([
    'price',
    'description',
    'updated_by',
    'assigned_id',
    'updated_at',
    'actions',
    'phones',
    'emails',
    'addresses',
    'clients',
  ]);

  private readonly shortNameLength = 28;
  private readonly shortFieldLabelLength = 18;
  private readonly shortFieldValueLength = 18;
  private readonly shortContactLength = 18;
  private readonly shortPriceLength = 14;

  protected readonly getContactName = getContactName;
  protected readonly getCompanyName = getCompanyName;

  has(key: string): boolean {
    return this.viewFields.includes(key);
  }

  hasAny(...keys: string[]): boolean {
    return keys.some((k) => this.viewFields.includes(k));
  }

  protected onContextMenu(event: MouseEvent): void {
    if (this.isTouchContextMenuDisabled) {
      event.preventDefault();
    }

    event.stopPropagation();
  }

  protected onCardActivate(): void {
    if (this.selectionMode) {
      this.selectionToggle.emit(this.card);
      return;
    }

    this.cardClick.emit(this.card);
  }

  protected onSelectionChange(): void {
    this.selectionToggle.emit(this.card);
  }

  /** Opens the contact; the card keeps its contacts in sync with what was changed there */
  protected onContactClick(event: MouseEvent, contact: BoardContact): void {
    event.stopPropagation();
    if (this.compact || this.selectionMode) return;

    this.contactService
      .openContactDetail(contact.id)
      .pipe(takeUntilDestroyed(this.destroyRef))
      .subscribe((result) => {
        if (!result?.contact && !result?.deleted) return;

        const stillLinked =
          !result.deleted && (result.contact?.cards?.some((card) => card.id === this.card.id) ?? true);
        const contacts = this.card.contacts ?? [];
        this.card.contacts = stillLinked
          ? contacts.map((item) => (item.id === contact.id ? result.contact! : item))
          : contacts.filter((item) => item.id !== contact.id);
      });
  }

  /** Opens the company; the card keeps its company in sync with what was changed there */
  protected onCompanyClick(event: MouseEvent, company: BoardCompany): void {
    event.stopPropagation();
    if (this.compact || this.selectionMode) return;

    this.companyService
      .openCompanyDetail(company.id)
      .pipe(takeUntilDestroyed(this.destroyRef))
      .subscribe((result) => {
        if (!result?.company && !result?.deleted) return;

        const stillLinked =
          !result.deleted && (result.company?.cards?.some((card) => card.id === this.card.id) ?? true);
        this.card.company = stillLinked ? (result.company ?? null) : null;
      });
  }

  protected onOpenMoveSidebar(event: MouseEvent): void {
    event.stopPropagation();
    this.cardMoveClick.emit(this.card);
  }

  get unknownFields(): BoardField[] {
    return this.viewFields
      .filter((k) => !this.KNOWN_KEYS.has(k))
      .map((k) => this.allFields.find((f) => this.fieldKey(f) === k))
      .filter((field): field is BoardField => !!field);
  }

  private hasTouchPointer(): boolean {
    if (typeof window === 'undefined') {
      return false;
    }

    return window.matchMedia?.('(pointer: coarse)').matches || navigator.maxTouchPoints > 0;
  }

  fieldKey(field: BoardField): string {
    return String(field.identifier ?? field.id ?? '');
  }

  getCustomFieldValue(key: string): unknown {
    return this.card.custom_fields?.[key] ?? null;
  }

  isLinkField(field: BoardField): boolean {
    return field.input_type === 'link';
  }

  isCheckboxField(field: BoardField): boolean {
    return field.input_type === 'checkbox';
  }

  isPriceField(field: BoardField): boolean {
    return field.input_type === 'price';
  }

  isPhoneField(field: BoardField): boolean {
    return field.input_type === 'phone';
  }

  isEmailField(field: BoardField): boolean {
    return field.input_type === 'email';
  }

  isDateField(field: BoardField): boolean {
    return field.input_type === 'date';
  }

  formatCustomFieldValue(field: BoardField): string {
    const value = this.getCustomFieldValue(this.fieldKey(field));
    if (value === null || value === undefined || value === '') return '—';
    if (this.isDateField(field)) return this.formatDateValue(value);
    if (this.isPhoneField(field)) {
      const digits = this.asString(value).replace(/\D/g, '');
      return digits ? `+${(digits.match(/\d{1,3}/g) ?? []).join(' ')}` : '—';
    }
    if (!this.isPriceField(field)) return this.asString(value);
    if (!isPriceFieldValue(value)) return this.asString(value);

    return formatPriceFieldValue(value);
  }

  displayCardName(): string {
    return this.truncateEnd(this.card.name, this.shortNameLength);
  }

  displayDescription(): string {
    return this.truncateEnd(this.card.description ?? '', this.shortFieldValueLength);
  }

  displaySystemPriceValue(): string {
    return this.truncateMiddle(this.systemPriceValue, this.shortPriceLength);
  }

  displayFieldName(field: BoardField): string {
    return this.truncateEnd(field.name ?? this.fieldKey(field), this.shortFieldLabelLength);
  }

  displayCustomFieldValue(field: BoardField): string {
    const value = this.formatCustomFieldValue(field);
    if (this.isEmailField(field) || this.isLinkField(field) || this.isPhoneField(field)) {
      return this.truncateMiddle(value, this.shortFieldValueLength);
    }

    return this.truncateEnd(value, this.shortFieldValueLength);
  }

  phoneValue(phone: ICardPhone): string {
    return getCardPhoneValue(phone);
  }

  displayPhone(phone: string): string {
    return this.truncateMiddle(phone, this.shortContactLength);
  }

  displayEmail(email: string): string {
    return this.truncateMiddle(email, this.shortContactLength);
  }

  addressValue(address: AddressInput): string {
    return formatAddress(address);
  }

  /** Street with the building, or the city, so the short value stays recognizable */
  displayAddress(address: AddressInput): string {
    const street = [address.street, address.building].filter(Boolean).join(', ');
    return this.truncateEnd(street || address.city || formatAddress(address), this.shortContactLength);
  }

  displayManagerName(user: BoardManager | null | undefined): string {
    return this.truncateEnd(this.getAvatarTitle(user), this.shortContactLength);
  }

  customFieldTitle(field: BoardField): string {
    const value = this.formatCustomFieldValue(field);
    return value === '—' ? '' : value;
  }

  private formatDateValue(value: unknown): string {
    if (typeof value !== 'string' || !value) return '—';
    if (/^\d{4}-\d{2}-\d{2}$/.test(value)) {
      const [year, month, day] = value.split('-');
      return `${day}.${month}.${year}`;
    }

    const date = new Date(value);
    if (Number.isNaN(date.getTime())) return value;
    return `${String(date.getDate()).padStart(2, '0')}.${String(date.getMonth() + 1).padStart(2, '0')}.${date.getFullYear()} ${String(date.getHours()).padStart(2, '0')}:${String(date.getMinutes()).padStart(2, '0')}`;
  }

  asString(val: unknown): string {
    if (val === null || val === undefined) return '';
    if (typeof val === 'string') return val;
    if (typeof val === 'number' || typeof val === 'boolean' || typeof val === 'bigint' || typeof val === 'symbol') {
      return val.toString();
    }
    if (Array.isArray(val))
      return val
        .map((item) => this.asString(item))
        .filter(Boolean)
        .join(', ');
    if (typeof val === 'object') {
      const record = val as Record<string, unknown>;
      const readable =
        record['name'] ?? record['title'] ?? record['label'] ?? record['value'] ?? record['phone'] ?? record['email'];
      if (readable !== undefined && readable !== null) return this.asString(readable);

      try {
        return JSON.stringify(val);
      } catch {
        return '';
      }
    }

    return '';
  }

  getPhoneLink(value: unknown): string {
    return `tel:${this.asString(value).replace(/[^\d+]/g, '')}`;
  }

  get currencySymbol(): string {
    return CURRENCY_SYMBOLS[this.card.currency] || this.card.currency;
  }

  get systemPriceValue(): string {
    return formatMoneyAmount(this.card.price, this.card.currency);
  }

  get updatedUser(): BoardManager | null {
    return this.card.updated_by_user || this.card.created_by_user;
  }

  get viewers(): CardViewer[] {
    return this.card.viewers ?? [];
  }

  get chats(): BoardCardChat[] {
    return this.card.chats ?? [];
  }

  get hasChats(): boolean {
    return this.chats.length > 0;
  }

  get unreadChatsCount(): number {
    const countedChatIds = new Set<string>();
    return this.chats.reduce((total, chat) => {
      if (countedChatIds.has(chat.id)) return total;

      countedChatIds.add(chat.id);
      return total + this.getChatUnreadCount(chat);
    }, 0);
  }

  get hasMarkedUnreadChat(): boolean {
    const checkedChatIds = new Set<string>();
    return this.chats.some((chat) => {
      if (checkedChatIds.has(chat.id)) return false;

      checkedChatIds.add(chat.id);
      return !this.getChatUnreadCount(chat) && !!chat.is_mark_unread;
    });
  }

  getChatUnreadCount(chat: BoardCardChat): number {
    const count = Number(chat.unread_count ?? 0);
    if (!Number.isFinite(count)) return 0;

    return Math.max(0, count);
  }

  formatUnreadCount(count: number): string {
    return count > 99 ? '99+' : String(count);
  }

  getChatTitle(chat: BoardCardChat): string {
    return chat.title?.trim() || '—';
  }

  getChatChannelName(chat: BoardCardChat): string {
    return chat.channel_name?.trim() || '—';
  }

  getChatChannelIcon(chat: BoardCardChat): string {
    if (!chat.channel_type) return '';

    const icon = CHANNEL_TYPES_OBJECT[chat.channel_type as CHANNEL_TYPE]?.img_url;
    return icon && icon !== 'UNKNOWN' ? icon : '';
  }

  getAvatarUrl(user: BoardManager | null | undefined): string | undefined {
    return user?.avatar_url || undefined;
  }

  getAvatarTitle(user: BoardManager | null | undefined): string {
    if (!user) return '—';
    return formatFullName(user.first_name, user.last_name);
  }

  private truncateEnd(value: string, maxLength: number): string {
    const normalized = this.normalizeText(value);
    if (!normalized || normalized.length <= maxLength) return normalized;
    return `${normalized.slice(0, Math.max(0, maxLength - 3))}...`;
  }

  private truncateMiddle(value: string, maxLength: number): string {
    const normalized = this.normalizeText(value);
    if (!normalized || normalized.length <= maxLength) return normalized;
    const edgeLength = Math.max(1, Math.floor((maxLength - 3) / 2));
    const endLength = Math.max(1, maxLength - 3 - edgeLength);
    return `${normalized.slice(0, edgeLength)}...${normalized.slice(-endLength)}`;
  }

  private normalizeText(value: string | null | undefined): string {
    return (value ?? '').replace(/\s+/g, ' ').trim();
  }
}
